'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useCategorySort } from '@eliadmin/core/src/hooks/category/use-category-sort';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { ProductOrder, ProductOrderBy } from '@eliadmin/core/src/utils/product-utils';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { Suspense } from 'react';
import { Breadcrumbs } from '../../components/homepage/breadcrumbs';

export default function SearchPage() {
  const locale = useLocaleStore(s => s.currentLocale);
  const { me, isInitialized } = useCustomerStore();
  const { selectedValue } = useCategorySort();

  const pathname = usePathname();
  const order = selectedValue.split(':')[1] as ProductOrder;
  const orderBy = selectedValue.split(':')[0] as ProductOrderBy;

  // legacy sync function removed; handled directly in onApply

  const query = buildProductsQuery({
    order,
    orderBy,
    results: 12,
    discounted: true,
  });

  const { data: products, isLoading } = useQuery({
    queryKey: [CacheKey.PRODUCTS, locale, me?.authToken ?? null, query, selectedValue],
    queryFn: () =>
      fetchProducts({
        locale,
        query,
        authToken: me?.authToken,
      }),
    enabled: isInitialized,
    refetchOnWindowFocus: false,
  });

  return (
    <>
      <section className={cn('custom-section', 'gap-12 pb-6 pt-12 sm:pb-8 md:pb-10 lg:pb-12')}>
        <div
          className={cn(
            'custom-container',
            'flex flex-col items-center justify-center gap-4 sm:gap-5 lg:gap-6',
          )}
        >
          <Breadcrumbs
            breadcrumb={{ name: 'Produkty v zľave', slug: pathname }}
            className={{
              wrapper: 'items-center justify-center',
              list: 'items-center justify-center',
            }}
          />
          <h1 className={cn('h2', 'w-full text-center font-medium tracking-tight text-foreground')}>
            Produkty v zľave
          </h1>
          {!isLoading && (
            <p className="w-full text-center text-xs sm:text-sm">
              {products?.meta?.total ?? 0} produktov
            </p>
          )}
        </div>
        <div className={cn('custom-container', 'hidden items-center justify-end sm:flex')}></div>
        <div className={cn('custom-container', 'flex flex-col items-start justify-start')}>
          {isLoading ? (
            <div className={cn('flex h-[300px] w-full items-center justify-center')}>
              <Spinner />
            </div>
          ) : (
            <div className="flex w-full flex-col items-center gap-6 sm:gap-8 lg:gap-12">
              <div
                className={cn(
                  'grid w-full grid-cols-2 gap-x-3 gap-y-6 md:grid-cols-3 md:gap-x-6 md:gap-y-12 xl:grid-cols-4',
                )}
              >
                {products?.data.map(product => (
                  <ProductCard key={`product-${product.id}`} product={product} />
                ))}
              </div>
              <Suspense>
                {products?.meta && <Pagination links={products.meta.links ?? []} />}
              </Suspense>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
